import { mergeOverrides, styled } from 'baseui'
import type { Theme } from 'baseui'
import type { Overrides } from 'baseui/helpers/overrides'
import type { StyleObject } from 'styletron-standard'
import { pulseLighting } from '@/shared/theme/pulseLighting'

interface ThemeProps {
  $theme: Theme
}

interface TabProps extends ThemeProps {
  $isActive: boolean
}

const TAB_EAR = '12px'

const pulseTabEar = (side: 'left' | 'right', color: string, visible: boolean): StyleObject => ({
  content: '""',
  opacity: visible ? 1 : 0,
  transitionProperty: 'opacity',
  transitionDuration: '150ms',
  transitionTimingFunction: 'ease',
  position: 'absolute',
  bottom: 0,
  [side]: `-${TAB_EAR}`,
  width: TAB_EAR,
  height: TAB_EAR,
  background: `radial-gradient(circle at ${side === 'left' ? '0' : '100%'} 0, transparent ${TAB_EAR}, ${color} calc(${TAB_EAR} + 0.5px))`,
  pointerEvents: 'none',
})

const pulseActiveTabStyle = ($theme: Theme): StyleObject => ({
  position: 'relative',
  height: '46px',
  marginBottom: 0,
  borderTopLeftRadius: '12px',
  borderTopRightRadius: '12px',
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  background: $theme.colors.bgPulseCanvas,
  color: $theme.colors.textPulseStrong,
  fontWeight: 600,
  '::before': pulseTabEar('left', $theme.colors.bgPulseCanvas, true),
  '::after': pulseTabEar('right', $theme.colors.bgPulseCanvas, true),
  ':hover': { background: $theme.colors.bgPulseCanvas, color: $theme.colors.textPulseStrong },
})

const pulseIdleTabStyle = ($theme: Theme): StyleObject => ({
  height: '40px',
  marginBottom: '4px',
  borderTopLeftRadius: '10px',
  borderTopRightRadius: '10px',
  borderBottomLeftRadius: '10px',
  borderBottomRightRadius: '10px',
  position: 'relative',
  background: $theme.colors.bgPulseTabRest,
  color: $theme.colors.textPulseLabel,
  fontWeight: 500,
  '::before': pulseTabEar('left', $theme.colors.bgPulseCanvas, false),
  '::after': pulseTabEar('right', $theme.colors.bgPulseCanvas, false),
  ':hover': { background: $theme.colors.bgPulseTabIdle, color: $theme.colors.primaryPulse },
})

const pulseTabStyle = ({ $theme, $isActive }: TabProps): StyleObject => ({
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: '18px',
  paddingRight: '18px',
  gap: '6px',
  fontSize: '15px',
  lineHeight: '20px',
  whiteSpace: 'nowrap',
  boxShadow: 'none',
  cursor: 'pointer',
  transitionProperty: 'background-color, color, height, margin-bottom, border-radius',
  transitionDuration: '150ms',
  transitionTimingFunction: 'ease',
  '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
  ...($isActive ? pulseActiveTabStyle($theme) : pulseIdleTabStyle($theme)),
})

const pulseDirtyTabStyle = ({ $theme, $isActive }: TabProps): StyleObject => {
  if ($isActive) {
    return { color: $theme.colors.dangerPulse, ':hover': { color: $theme.colors.dangerPulse } }
  }

  const { shadowPulseDirtyTab, shadowPulseDirtyRingStart, shadowPulseDirtyRingEnd } = pulseLighting($theme)

  return {
    background: $theme.colors.dangerPulse,
    color: $theme.colors.white,
    fontWeight: 600,
    boxShadow: shadowPulseDirtyTab,
    animationName: {
      '0%': { boxShadow: shadowPulseDirtyRingStart },
      '70%': { boxShadow: shadowPulseDirtyRingEnd },
      '100%': { boxShadow: shadowPulseDirtyRingEnd },
    },
    animationDuration: '1.8s',
    animationTimingFunction: 'ease',
    animationIterationCount: 'infinite',
    ':hover': {
      background: $theme.colors.dangerPulse,
    },
    '@media (prefers-reduced-motion: reduce)': {
      animationName: 'none',
    },
  }
}

const pulseStripCorner = (side: 'left' | 'right', color: string, visible = true): StyleObject => ({
  content: '""',
  opacity: visible ? 1 : 0,
  transitionProperty: 'opacity',
  transitionDuration: '150ms',
  transitionTimingFunction: 'ease',
  position: 'absolute',
  top: '100%',
  [side]: 0,
  zIndex: 1,
  width: TAB_EAR,
  height: TAB_EAR,
  background: `radial-gradient(circle at ${side === 'left' ? '100%' : '0'} 100%, transparent ${TAB_EAR}, ${color} calc(${TAB_EAR} + 0.5px))`,
  pointerEvents: 'none',
})

export const pulseTabsOverrides = (tabsOverflowing: boolean, firstTabActive = false) => ({
  TabBorder: {
    style: { height: 0 },
  },
  TabHighlight: {
    style: { display: 'none' },
  },
  TabBar: {
    style: ({ $theme }: ThemeProps) => ({
      display: 'flex',
      alignItems: 'flex-end',
      flexWrap: 'nowrap',
      columnGap: 0,
      position: 'relative',
      paddingTop: '10px',
      paddingRight: '12px',
      backgroundColor: $theme.colors.bgPulseTabStrip,
      '::before': pulseStripCorner('left', $theme.colors.bgPulseTabStrip, !firstTabActive),
      '::after': pulseStripCorner('right', $theme.colors.bgPulseTabStrip),
    }),
  },
  TabList: {
    style: {
      alignItems: 'flex-end',
      minHeight: '46px',
      flexGrow: 0,
      flexShrink: 1,
      minWidth: 0,
      gap: '4px',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '12px',
      paddingRight: '12px',
      marginBottom: 0,
      backgroundColor: 'transparent',
    },
  },
  EndEnhancerContainer: {
    style: {
      flexGrow: 1,
      flexShrink: 0,
      flexBasis: 'auto',
      justifyContent: 'flex-start',
      alignItems: 'flex-end',
      gap: '14px',
      marginLeft: tabsOverflowing ? '4px' : '-8px',
      marginBottom: '4px',
    },
  },
})

const pulsePanelEntrance: StyleObject = {
  animationName: {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  animationDuration: '300ms',
  animationTimingFunction: 'ease',
  '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
}

const pulseAdditionalSalesTabPanelStyle = ({ $theme }: ThemeProps): StyleObject => ({
  backgroundColor: $theme.colors.bgPulseCanvas,
  borderTopWidth: '20px',
  borderBottomWidth: '24px',
  borderLeftWidth: '24px',
  borderRightWidth: '24px',
  borderTopStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderRightStyle: 'solid',
  borderTopColor: $theme.colors.bgPulseCanvas,
  borderBottomColor: $theme.colors.bgPulseCanvas,
  borderLeftColor: $theme.colors.bgPulseCanvas,
  borderRightColor: $theme.colors.bgPulseCanvas,
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  display: 'flex',
  alignItems: 'flex-start',
  gap: '48px',
  alignSelf: 'stretch',
  ...pulsePanelEntrance,
})

const pulseCanvasTabPanelStyle = ({ $theme }: ThemeProps): StyleObject => ({
  ...pulsePanelEntrance,
  backgroundColor: $theme.colors.bgPulseCanvas,
  paddingTop: '20px',
  paddingBottom: '24px',
  paddingLeft: '24px',
  paddingRight: '24px',
})

export const pulseTabOverrides = {
  Tab: { style: pulseTabStyle },
  TabPanel: { style: pulseAdditionalSalesTabPanelStyle },
}

const pulseAdditionalSalesV2TabPanelStyle = ({ $theme }: ThemeProps): StyleObject => ({
  ...pulsePanelEntrance,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: $theme.colors.bgPulseCanvas,
  paddingTop: '10px',
  paddingBottom: '10px',
  paddingLeft: '10px',
  paddingRight: '10px',
})

export const pulseAdditionalSalesV2TabOverrides = {
  Tab: { style: pulseTabStyle },
  TabPanel: { style: pulseAdditionalSalesV2TabPanelStyle },
}

export const pulseContractTabOverrides = {
  Tab: { style: pulseTabStyle },
  TabPanel: { style: pulseCanvasTabPanelStyle },
}

export const pulseClientTabOverrides = (hasUnsavedChanges: boolean) => ({
  Tab: {
    style: (props: TabProps) => ({
      ...pulseTabStyle(props),
      ...(hasUnsavedChanges ? pulseDirtyTabStyle(props) : {}),
    }),
  },
  TabPanel: { style: pulseCanvasTabPanelStyle },
})

const pulseAddButtonBorder = (color: string) => ({
  borderTopWidth: '1px',
  borderBottomWidth: '1px',
  borderLeftWidth: '1px',
  borderRightWidth: '1px',
  borderTopStyle: 'solid' as const,
  borderBottomStyle: 'solid' as const,
  borderLeftStyle: 'solid' as const,
  borderRightStyle: 'solid' as const,
  borderTopColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
  borderRightColor: color,
})

export const pulseAddButtonOverrides = {
  BaseButton: {
    style: ({ $theme }: ThemeProps) => ({
      height: '40px',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: '14px',
      paddingRight: '14px',
      marginLeft: 0,
      marginRight: 0,
      backgroundColor: $theme.colors.white,
      color: $theme.colors.primaryPulse,
      ...pulseAddButtonBorder($theme.colors.borderPulseButton),
      fontSize: '15px',
      fontWeight: 500,
      borderTopLeftRadius: '10px',
      borderTopRightRadius: '10px',
      borderBottomLeftRadius: '10px',
      borderBottomRightRadius: '10px',
      transitionProperty: 'border-color',
      transitionDuration: '150ms',
      transitionTimingFunction: 'ease',
      '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
      ':hover': {
        backgroundColor: $theme.colors.white,
        color: $theme.colors.primaryPulse,
        ...pulseAddButtonBorder($theme.colors.borderPulseControl),
      },
    }),
  },
}

export const TabsFrame = styled<'div', { $pulse: boolean }>('div', ({ $theme, $pulse }) =>
  $pulse
    ? {
        fontFamily: $theme.typography.font300.fontFamily,
        backgroundColor: $theme.colors.bgPulseCanvas,
        borderTopLeftRadius: '12px',
        borderTopRightRadius: '12px',
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        overflow: 'hidden',
        boxSizing: 'border-box',
      }
    : {},
)

export const ClientTabColumns = styled<'div', { $pulse: boolean; $columns: number }>('div', ({ $pulse, $columns }) =>
  $pulse
    ? {
        width: '100%',
        display: 'grid',
        gridTemplateColumns: `repeat(${$columns}, minmax(0, 1fr))`,
        gap: '20px',
        alignItems: 'start',
        marginTop: '16px',
      }
    : {
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: '48px',
      },
)

export const ClientTabSideColumns = styled<'div', { $stacked: boolean }>('div', ({ $stacked }) =>
  $stacked ? { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 } : { display: 'contents' },
)

export const ClientTabColumn = styled<'div', { $pulse: boolean }>('div', ({ $pulse }) =>
  $pulse ? { display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 } : { width: '100%' },
)

export const PulseTabLabelFrame = styled<'span', { $iconAfterText?: boolean }>('span', ({ $iconAfterText }) => ({
  display: 'inline-flex',
  flexDirection: $iconAfterText ? 'row-reverse' : 'row',
  alignItems: 'center',
  gap: '8px',
}))

export const pulseTabIconColors = ($theme: Theme, $active: boolean, $alert: boolean) => {
  if ($alert)
    return {
      backgroundColor: $active ? $theme.colors.dangerPulseBg : 'transparent',
      color: $active ? $theme.colors.dangerPulse : 'inherit',
    }
  return {
    backgroundColor: $active ? $theme.colors.borderPulseCard : 'transparent',
    color: $active ? $theme.colors.primaryPulse : 'inherit',
  }
}

export const PulseTabIconSlot = styled<'span', { $active: boolean; $alert?: boolean }>(
  'span',
  ({ $theme, $active, $alert = false }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderTopLeftRadius: '7px',
    borderTopRightRadius: '7px',
    borderBottomLeftRadius: '7px',
    borderBottomRightRadius: '7px',
    ...pulseTabIconColors($theme, $active, $alert),
    transitionProperty: 'background-color, color',
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease',
    '@media (prefers-reduced-motion: reduce)': { transitionDuration: '0ms' },
    ':focus-visible': {
      outlineWidth: '2px',
      outlineStyle: 'solid',
      outlineColor: 'currentColor',
      outlineOffset: '2px',
    },
  }),
)

export const PulseTabClose = styled('span', ({ $theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '22px',
  height: '22px',
  paddingTop: 0,
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  borderTopWidth: 0,
  borderBottomWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  borderTopLeftRadius: '6px',
  borderTopRightRadius: '6px',
  borderBottomLeftRadius: '6px',
  borderBottomRightRadius: '6px',
  backgroundColor: 'transparent',
  color: 'inherit',
  cursor: 'pointer',
  ':hover': {
    backgroundColor: $theme.colors.bgPulseTabIdle,
    color: $theme.colors.dangerPulse,
  },
}))

export const PulseClientToolbar = styled<'div', { $hidden: boolean }>('div', ({ $hidden }) => ({
  display: $hidden ? 'none' : 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: '12px',
}))

const extendStyle = <T extends object>(overrides: T, key: string, extra: StyleObject): T =>
  mergeOverrides(overrides as Overrides, { [key]: { style: () => extra } }) as T

export const fillingTabsOverrides = <T extends object>(overrides: T): T =>
  extendStyle(overrides, 'Root', { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 })

export const fillingTabPanelOverrides = <T extends object>(overrides: T): T =>
  extendStyle(overrides, 'TabPanel', { flex: '1 1 auto', minHeight: 0, overflowY: 'auto' })
